/**
 * Text formatting utilities for feedback text
 * Handles markdown-like formatting: **bold**, \n\n (newlines), etc.
 */

/**
 * Converts markdown-like formatting to HTML
 * Supports:
 * - **text** → <strong>text</strong>
 * - \n\n → <br /><br /> (double newline for paragraph breaks)
 * - \n → <br /> (single newline)
 *
 * @param text - Raw text with markdown-like formatting
 * @returns HTML string with formatted text
 */
export function formatFeedbackText(text: string): string {
  if (!text || typeof text !== 'string') {
    return ''
  }

  let formatted = text

  // Escape existing HTML to prevent XSS
  formatted = escapeHtml(formatted)

  // Convert **bold** to <strong>bold</strong>
  // Match **text** but not ***text*** (which might be intended as bold+italic)
  formatted = formatted.replace(/\*\*([^*]+?)\*\*(?!\*)/g, '<strong>$1</strong>')

  // Convert double newlines (\n\n) to paragraph breaks (<br /><br />)
  formatted = formatted.replace(/\n\n/g, '<br /><br />')

  // Convert single newlines (\n) to line breaks (<br />)
  formatted = formatted.replace(/\n/g, '<br />')

  return formatted
}

/**
 * Escape HTML special characters to prevent XSS
 * @param text - Text to escape
 * @returns Escaped HTML string
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }

  return text.replace(/[&<>"']/g, (char) => map[char] || char)
}

/**
 * Check if text contains formatting markers
 * @param text - Text to check
 * @returns True if text contains formatting markers
 */
export function hasFormatting(text: string): boolean {
  if (!text || typeof text !== 'string') {
    return false
  }

  return /\*\*.*\*\*/.test(text) || /\n/.test(text)
}
